import { FormEvent, useEffect, useState } from 'react';
import AdminLayout from '../AdminLayout';
import { createPartner, deletePartner, fetchAdminPartners, reorderPartners, updatePartner } from '../adminService';
import { useAdminSession } from '../adminSession';
import { ApiError, apiUrl } from '../../../services/apiClient';
import { AdminPartner, PartnerInput } from '../../Company/partnerService';
import formStyles from '../Portfolio/AdminPortfolioForm.module.scss';
import listStyles from '../Portfolio/AdminPortfolio.module.scss';
import styles from './AdminPartners.module.scss';

const EMPTY_FORM: PartnerInput = {
  name: '',
  field: '',
  url: '',
  isPublished: true,
};

// 회사소개 > 협력사(/company/partners)에 노출되는 협력사 관리
function AdminPartners() {
  const [session, setSession] = useAdminSession();
  const [items, setItems] = useState<AdminPartner[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  // null: 편집 안 함, 'new': 새 협력사, 숫자: 해당 id 수정
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<PartnerInput>(EMPTY_FORM);
  const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleError = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 401) {
      setSession(null);
    } else {
      setError(err instanceof Error ? err.message : fallback);
    }
  };

  const load = () => {
    if (!session) return;
    fetchAdminPartners(session.token)
      .then(setItems)
      .catch((err) => handleError(err, '협력사 목록을 불러오지 못했습니다.'));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [session]);

  useEffect(
    () => () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    },
    [logoPreview],
  );

  const handleLogoChange = (file: File | null) => {
    setLogoFile(file);
    setLogoPreview(file ? URL.createObjectURL(file) : null);
    if (file) setRemoveLogo(false);
  };

  const startEdit = (item: AdminPartner | null) => {
    setFormError(null);
    handleLogoChange(null);
    setRemoveLogo(false);
    if (item) {
      setEditing(item.id);
      setExistingLogoUrl(item.logoUrl);
      setForm({
        name: item.name,
        field: item.field ?? '',
        url: item.url ?? '',
        isPublished: item.isPublished,
      });
    } else {
      setEditing('new');
      setExistingLogoUrl(null);
      setForm(EMPTY_FORM);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setField = <K extends keyof PartnerInput>(key: K, value: PartnerInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!session || editing === null) return;
    if (!form.name.trim()) {
      setFormError('협력사명을 입력해주세요.');
      return;
    }

    setFormError(null);
    setIsBusy(true);
    try {
      const saved =
        editing === 'new'
          ? await createPartner(session.token, form, logoFile)
          : await updatePartner(session.token, editing, form, logoFile, removeLogo);
      setItems((prev) =>
        editing === 'new' ? [saved, ...(prev ?? [])] : (prev ?? []).map((item) => (item.id === saved.id ? saved : item)),
      );
      setEditing(null);
      handleLogoChange(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSession(null);
      } else {
        setFormError(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.');
      }
    } finally {
      setIsBusy(false);
    }
  };

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    setIsBusy(true);
    try {
      await action();
    } catch (err) {
      handleError(err, '처리 중 오류가 발생했습니다.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleMove = (index: number, offset: -1 | 1) => {
    if (!session || !items) return;
    const ids = items.map((item) => item.id);
    const target = index + offset;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    run(async () => setItems(await reorderPartners(session.token, ids)));
  };

  const handleDelete = (item: AdminPartner) => {
    if (!session) return;
    if (!window.confirm(`"${item.name}" 협력사를 삭제하시겠습니까? 되돌릴 수 없습니다.`)) return;
    run(async () => {
      await deletePartner(session.token, item.id);
      setItems((prev) => (prev ?? []).filter((i) => i.id !== item.id));
      if (editing === item.id) setEditing(null);
    });
  };

  const previewUrl = logoPreview ?? (existingLogoUrl && !removeLogo ? apiUrl(existingLogoUrl) : null);

  return (
    <AdminLayout title="협력사 관리">
      {editing !== null && (
        <div className="section">
          <div className="sec-head">
            <div className="sec-title">{editing === 'new' ? '새 협력사 등록' : '협력사 수정'}</div>
            <div className="sec-actions">
              <button type="button" className="btn btn-outline" onClick={() => setEditing(null)}>
                취소
              </button>
            </div>
          </div>

          <form className={formStyles.form} onSubmit={handleSubmit}>
            <div className="card">
              <div className={formStyles.grid}>
                <div className={formStyles.field}>
                  <label>
                    협력사명<span className={formStyles.required}>*</span>
                  </label>
                  <input
                    value={form.name}
                    maxLength={100}
                    onChange={(e) => setField('name', e.target.value)}
                    placeholder="예: 지웍스인쇄"
                  />
                </div>
                <div className={formStyles.field}>
                  <label>협력 분야</label>
                  <input
                    value={form.field}
                    maxLength={100}
                    onChange={(e) => setField('field', e.target.value)}
                    placeholder="예: 인쇄 · 후가공"
                  />
                </div>
                <div className={formStyles.field}>
                  <label>홈페이지 주소</label>
                  <input
                    value={form.url}
                    maxLength={255}
                    onChange={(e) => setField('url', e.target.value)}
                    placeholder="예: www.example.co.kr"
                  />
                </div>
                <div className={formStyles.field}>
                  <label className={formStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.isPublished}
                      onChange={(e) => setField('isPublished', e.target.checked)}
                    />
                    협력사 페이지에 공개
                  </label>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-title" style={{ marginBottom: 12 }}>
                로고 이미지
              </div>
              {previewUrl && <img src={previewUrl} alt="미리보기" className={styles.logoPreview} />}
              <div className={styles.logoActions}>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => handleLogoChange(e.target.files?.[0] ?? null)}
                />
                {existingLogoUrl && !logoFile && (
                  <label className={formStyles.checkboxLabel}>
                    <input type="checkbox" checked={removeLogo} onChange={(e) => setRemoveLogo(e.target.checked)} />
                    기존 로고 삭제
                  </label>
                )}
              </div>
              <p className={styles.hint}>
                jpg · png · gif · webp, 최대 20MB. 배경이 투명한 png를 권장합니다. 로고가 없으면 협력사명이 표시됩니다.
              </p>
            </div>

            {formError && (
              <div className="card" style={{ color: '#B91C1C' }}>
                {formError}
              </div>
            )}

            <div className={formStyles.submitRow}>
              <button type="submit" className="btn btn-primary" disabled={isBusy}>
                {isBusy ? '저장 중...' : editing === 'new' ? '등록하기' : '저장하기'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="section">
        <div className="sec-head">
          <div className="sec-title">협력사 {items?.length ?? 0}곳</div>
          <div className="sec-actions">
            <button type="button" className="btn btn-primary" onClick={() => startEdit(null)}>
              + 새 협력사 등록
            </button>
          </div>
        </div>

        {error && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>순서</th>
                  <th>로고</th>
                  <th>협력사명</th>
                  <th>협력 분야</th>
                  <th>홈페이지</th>
                  <th>공개</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {!items && (
                  <tr>
                    <td colSpan={7} className="emptyRow">
                      불러오는 중...
                    </td>
                  </tr>
                )}
                {items && items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="emptyRow">
                      등록된 협력사가 없습니다.
                    </td>
                  </tr>
                )}
                {items?.map((item, index) => (
                  <tr key={item.id} onClick={() => startEdit(item)}>
                    <td onClick={(event) => event.stopPropagation()}>
                      <div className={listStyles.rowActions}>
                        <button
                          type="button"
                          className="btn btn-outline"
                          aria-label="위로"
                          disabled={isBusy || index === 0}
                          onClick={() => handleMove(index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline"
                          aria-label="아래로"
                          disabled={isBusy || index === items.length - 1}
                          onClick={() => handleMove(index, 1)}
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                    <td>
                      {item.logoUrl ? (
                        <img src={apiUrl(item.logoUrl)} alt={item.name} className={styles.thumb} />
                      ) : (
                        <span className={styles.noLogo}>없음</span>
                      )}
                    </td>
                    <td className={styles.nameCell}>{item.name}</td>
                    <td>{item.field ?? '-'}</td>
                    <td className={styles.urlCell}>{item.url ?? '-'}</td>
                    <td>
                      <span className={`badgePill ${item.isPublished ? 'ok' : 'neutral'}`}>
                        <span className="pulse"></span> {item.isPublished ? '공개' : '비공개'}
                      </span>
                    </td>
                    <td onClick={(event) => event.stopPropagation()}>
                      <div className={listStyles.rowActions}>
                        <button type="button" className="btn btn-outline" onClick={() => startEdit(item)}>
                          수정
                        </button>
                        <button
                          type="button"
                          className={`btn btn-outline ${listStyles.dangerBtn}`}
                          disabled={isBusy}
                          onClick={() => handleDelete(item)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className={styles.hint} style={{ marginTop: 10 }}>
          순서는 회사소개 &gt; 협력사 페이지에 보이는 순서입니다.
        </p>
      </div>
    </AdminLayout>
  );
}

export default AdminPartners;
